import type {CompleteMeshData, AbstractMeshData} from "#template/interfaces/mesh-data"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"

const TD = TypeDescriptors

const generateMeshDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    computeDummyReifiedMeshData: TD.inlet(TD.Boolean),
    inputMeshData: TD.inlet(TD.AbstractMeshData),
    completeMeshData: TD.outlet(TD.CompleteMeshData),
}

export class GenerateMesh implements NodeClassImpl<typeof generateMeshDescriptor, typeof GenerateMesh> {
    static descriptor = generateMeshDescriptor
    static uniqueName = "GenerateMesh"
    sceneManager!: Inlet<ISceneManager>
    computeDummyReifiedMeshData!: Inlet<boolean>
    inputMeshData!: Inlet<AbstractMeshData>
    completeMeshData!: Outlet<CompleteMeshData>

    private pending: Subscription | null = null

    run() {
        this.cleanup()

        const {sceneManager, inputMeshData, completeMeshData} = this

        if (sceneManager === NotReady || inputMeshData === NotReady || this.computeDummyReifiedMeshData === NotReady) {
            this.completeMeshData.emitIfChanged(NotReady)
            return
        }

        this.pending = sceneManager.addTask({
            description: `evaluate${this.computeDummyReifiedMeshData ? "Dummy" : ""}Mesh(${inputMeshData.contentHash})`,
            task: defer(() =>
                this.computeDummyReifiedMeshData
                    ? sceneManager.evaluateMesh(
                          {
                              type: "empty",
                          },
                          {},
                      )
                    : sceneManager.evaluateMesh(inputMeshData.displayGeometryGraph, inputMeshData.displayGeometryGraphResources),
            ).pipe(
                map((reified) => {
                    this.completeMeshData.emitIfChanged({
                        reified,
                        abstract: inputMeshData,
                    })
                }),
            ),
            critical: true,
        })
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
